<template>
    <div class="relative min-h-screen flex flex-col items-center justify-start overflow-hidden">
        <!-- Blurred artwork background -->
        <div
            class="fixed inset-0 bg-cover bg-center scale-110 blur-3xl opacity-40 transition-all duration-1000"
            :style="artworkUrl ? { backgroundImage: `url(${artworkUrl})` } : {}"
        />
        <!-- Dark overlay gradient -->
        <div class="fixed inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80" />

        <!-- Content -->
        <div class="relative z-10 w-full max-w-md mx-auto px-4 flex flex-col items-center gap-6 pt-12 pb-16">

            <!-- Artwork + Play Button -->
            <div class="relative group">
                <div class="w-52 h-52 rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10 relative">
                    <img
                        v-if="artworkUrl"
                        :src="artworkUrl"
                        :alt="shoplink?.name"
                        width="208"
                        height="208"
                        fetchpriority="high"
                        decoding="async"
                        class="w-full h-full object-cover"
                    />
                    <div v-else class="w-full h-full bg-white/5 flex items-center justify-center">
                        <el-icon class="text-6xl text-white/20"><component :is="Headset" /></el-icon>
                    </div>

                    <!-- Centered Play/Pause Button (Always visible) -->
                    <button
                        v-if="currentAudioUrl"
                        class="absolute inset-0 flex items-center justify-center bg-black/10 hover:bg-black/20 transition-all duration-500 group/overlay"
                        @click="togglePlay"
                    >
                        <div 
                            class="w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 active:scale-95 shadow-xl group/btn"
                            :class="[
                                isPlaying 
                                    ? 'bg-white/10 border border-white/20 backdrop-blur-md' 
                                    : 'bg-gradient-to-br from-[#00f2ff] to-[#00c8e0] shadow-[0_0_25px_rgba(0,242,255,0.4)] hover:shadow-[0_0_35px_rgba(0,242,255,0.6)]'
                            ]"
                        >
                            <svg v-if="!isPlaying" viewBox="0 0 24 24" class="w-8 h-8 text-black ml-1 transition-transform group-hover/btn:scale-110" fill="currentColor">
                                <path d="M8 5v14l11-7z" />
                            </svg>
                            <svg v-else viewBox="0 0 24 24" class="w-8 h-8 text-[#00f2ff] transition-transform group-hover/btn:scale-110" fill="currentColor">
                                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                            </svg>
                        </div>
                    </button>
                </div>
            </div>

            <!-- Track selector for albums/EPs -->
            <div v-if="shoplink?.tracks?.length > 1" class="w-full">
                <p class="text-[10px] font-bold text-white/40 uppercase tracking-widest mb-2 text-center">Preview Track</p>
                <div class="flex flex-wrap gap-2 justify-center">
                    <button
                        v-for="(track, i) in shoplink.tracks"
                        :key="track._id"
                        class="px-3 py-1.5 rounded-full text-xs font-bold transition-all border"
                        :class="selectedTrackIdx === i
                            ? 'bg-white text-black border-white'
                            : 'bg-white/5 text-white/60 border-white/10 hover:bg-white/10'"
                        @click="selectTrack(i)"
                    >
                        {{ trackDisplayTitle(track) }}
                    </button>
                </div>
            </div>

            <!-- Release Info -->
            <div class="text-center">
                <h1 class="text-white font-black text-2xl leading-tight drop-shadow-lg">{{ displayName }}</h1>
                <p class="text-white/60 text-sm mt-1 font-medium">{{ displayArtist }}</p>
            </div>

            <!-- Pre-Release Countdown + Pre-Save -->
            <div v-if="isUpcoming" class="w-full bg-gradient-to-br from-[#1DB954]/15 to-[#1DB954]/5 backdrop-blur-xl border border-[#1DB954]/30 rounded-lg overflow-hidden shadow-2xl">
                <div class="px-5 py-3 border-b border-[#1DB954]/20 flex items-center justify-between">
                    <p class="text-[10px] font-black text-[#1DB954] uppercase tracking-widest">Out In</p>
                    <p class="text-[10px] font-bold text-white/40">{{ formattedReleaseDate }}</p>
                </div>
                <div class="p-5 flex flex-col items-center gap-4">
                    <div class="grid grid-cols-4 gap-2 w-full text-center">
                        <div v-for="unit in countdownUnits" :key="unit.label" class="bg-white/5 border border-white/10 rounded-lg py-3">
                            <div class="text-2xl font-black text-white tabular-nums">{{ String(unit.value).padStart(2, '0') }}</div>
                            <div class="text-[9px] font-bold uppercase tracking-widest text-white/40 mt-1">{{ unit.label }}</div>
                        </div>
                    </div>
                    <button
                        v-if="!presaved"
                        class="w-full py-3.5 rounded-full bg-[#1DB954] hover:bg-[#1ed760] text-black font-black uppercase tracking-widest text-sm transition-all flex items-center justify-center gap-2 shadow-[0_0_25px_rgba(29,185,84,0.4)] active:scale-[0.98]"
                        @click="startPresave"
                    >
                        <img alt="spotify" :src="getDspLogo('spotify')" v-if="getDspLogo('spotify')" class="w-5 h-5" />
                        Pre-Save on Spotify
                    </button>
                    <div v-else class="w-full py-3.5 rounded-full bg-white/5 border border-[#1DB954]/40 text-[#1DB954] font-black uppercase tracking-widest text-sm flex items-center justify-center gap-2">
                        <el-icon><CircleCheck /></el-icon>
                        Pre-Saved
                    </div>
                </div>
            </div>

            <!-- Store Links -->
            <div v-if="!isUpcoming && shoplink?.links?.length" class="w-full bg-white/5 backdrop-blur-xl border border-white/10 rounded-lg overflow-hidden shadow-2xl">
                <div class="px-5 py-3 border-b border-white/5">
                    <p class="text-[10px] font-black text-white/40 uppercase tracking-widest">Listen On</p>
                </div>
                <div class="divide-y divide-white/5">
                    <a
                        v-for="link in shoplink.links"
                        :key="link.platform"
                        :href="link.url"
                        target="_blank"
                        rel="noopener"
                        class="flex items-center justify-between px-5 py-4 hover:bg-white/5 transition-colors group"
                    >
                        <div class="flex items-center gap-3">
                            <div class="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                                <img :alt="link.platform" v-if="getDspLogo(link.platform)" :src="getDspLogo(link.platform)" class="w-5 h-5 object-contain" />
                                <el-icon v-else class="text-white/30"><component :is="Link" /></el-icon>
                            </div>
                            <span class="text-white font-semibold text-sm capitalize">{{ formatPlatformName(link.platform) }}</span>
                        </div>
                        <span
                            class="text-[10px] font-black uppercase tracking-wider transition-colors"
                            :class="isOwnShopPlatform(link.platform)
                                ? 'text-primary/70 group-hover:text-primary'
                                : 'text-white/30 group-hover:text-white/60'"
                        >{{ isOwnShopPlatform(link.platform) ? 'Buy →' : 'Listen →' }}</span>
                    </a>
                </div>
            </div>

            <!-- Download Gate -->
            <div v-if="shoplink?.downloadGate?.enabled" class="w-full">
                <div class="bg-gradient-to-br from-purple-500/10 to-purple-900/20 backdrop-blur-xl border border-purple-500/20 rounded-lg overflow-hidden shadow-2xl">
                    <div class="px-5 py-4 border-b border-purple-500/10 flex items-center gap-3">
                        <div class="w-8 h-8 rounded-xl bg-purple-500/20 flex items-center justify-center">
                            <el-icon class="text-purple-400"><component :is="Download" /></el-icon>
                        </div>
                        <div>
                            <p class="text-white font-bold text-sm">Free Download</p>
                            <p class="text-white/40 text-xs">Complete {{ shoplink.downloadGate.tasks?.length === 1 ? 'the task' : 'all tasks' }} below to unlock</p>
                        </div>
                    </div>

                    <div class="p-5 space-y-3">
                        <div v-if="!isFullyUnlocked" class="space-y-3">
                            <div
                                v-for="(task, i) in shoplink.downloadGate.tasks"
                                :key="task._id"
                                class="flex items-center gap-3 p-3.5 rounded-lg bg-white/5 border border-white/10 transition-all"
                                :class="{ 'border-purple-500/50 bg-purple-500/5': completedTaskIds.includes(task._id) }"
                            >
                                <div class="w-8 h-8 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                                    <img :alt="task.platform" v-if="getDspLogo(task.platform)" :src="getDspLogo(task.platform)" class="w-5 h-5 object-contain" />
                                </div>
                                <div class="flex-1 min-w-0">
                                    <p class="text-white text-sm font-semibold">{{ getTaskLabel(task) }}</p>
                                    <div v-if="task.items?.length" class="flex flex-wrap gap-1.5 mt-1">
                                        <span
                                            v-for="item in task.items.slice(0, 3)"
                                            :key="item.id"
                                            class="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/50"
                                        >{{ item.name }}</span>
                                    </div>
                                </div>
                                
                                <!-- Task Status/Action -->
                                <div v-if="completedTaskIds.includes(task._id)" class="bg-purple-500 rounded-full p-1.5">
                                    <el-icon class="text-white text-xs"><component :is="CircleCheck" /></el-icon>
                                </div>
                                <button 
                                    v-else 
                                    class="px-4 py-1.5 rounded-full bg-white/10 border border-white/10 text-white text-[10px] font-black hover:bg-white/20 transition-all flex items-center gap-2"
                                    :loading="verifyingTaskId === task._id || connectingTaskId === task._id"
                                    :disabled="connectingTaskId !== null"
                                    @click="handleTaskAction(task)"
                                >
                                    {{ isPlatformConnected(task.platform) 
                                        ? (verifyingTaskId === task._id ? 'Following...' : 'Complete Task') 
                                        : (connectingTaskId === task._id ? 'Connecting...' : 'Connect') }}
                                </button>
                            </div>

                            <p v-if="completedTaskIds.length > 0 && !isFullyUnlocked" class="text-center text-[10px] text-white/40 font-bold uppercase tracking-widest mt-4">
                                {{ shoplink.downloadGate.tasks.length - completedTaskIds.length }} task(s) remaining
                            </p>
                        </div>

                        <!-- Fully Unlocked State: Download Buttons -->
                        <div v-else class="space-y-3 pt-2">
                            <div class="text-center pb-2">
                                <el-icon class="text-green-400 text-3xl mb-1"><component :is="CircleCheck" /></el-icon>
                                <p class="text-white font-bold text-sm">Gate Unlocked!</p>
                                <p class="text-white/40 text-[10px]">Your downloads are ready below.</p>
                            </div>

                            <div v-for="track in shoplink.tracks" :key="track._id" class="p-4 rounded-lg bg-white/5 border border-white/10 flex flex-col gap-3">
                                <div class="flex items-center justify-between">
                                    <span class="text-white font-bold text-sm truncate pr-4">{{ trackDisplayTitle(track) }}</span>
                                    <span class="text-[10px] text-white/30 font-medium">{{ formatDuration(track.duration) }}</span>
                                </div>
                                <div class="grid grid-cols-2 gap-2">
                                    <button
                                        @click="downloadTrack(track._id, 'mp3')"
                                        :disabled="downloadingTrackId === track._id"
                                        class="py-2.5 rounded-xl bg-purple-500/20 border border-purple-500/40 text-purple-300 text-[11px] font-black hover:bg-purple-500/30 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <el-icon><component :is="Download" /></el-icon> {{ downloadingTrackId === track._id ? 'Downloading...' : 'MP3' }}
                                    </button>
                                    <button
                                        v-if="shoplink.downloadGate.allowWav && track.hasWav"
                                        @click="downloadTrack(track._id, 'wav')"
                                        :disabled="downloadingTrackId === track._id"
                                        class="py-2.5 rounded-xl bg-green-500/20 border border-green-500/40 text-green-300 text-[11px] font-black hover:bg-green-500/30 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <el-icon><component :is="Download" /></el-icon> {{ downloadingTrackId === track._id ? 'Downloading...' : 'WAV' }}
                                    </button>
                                    <button
                                        v-else-if="!shoplink.downloadGate.allowWav"
                                        disabled
                                        class="py-2.5 rounded-xl bg-white/5 border border-white/10 text-white/30 text-[11px] font-black cursor-not-allowed flex items-center justify-center gap-2"
                                    >
                                        <el-icon><component :is="Download" /></el-icon> WAV
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>

        <!-- Hidden Audio Element -->
        <audio
            v-if="currentAudioUrl"
            ref="audioPlayer"
            :src="currentAudioUrl"
            preload="auto"
            @ended="isPlaying = false"
            @play="isPlaying = true"
            @pause="isPlaying = false"
        />
    </div>
</template>

<script setup>
import { ref, computed, onBeforeUnmount, onMounted, watch } from 'vue'
import { VideoPause, VideoPlay, Headset, Link, Download, CircleCheck } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { isOwnShopPlatform, useShoplinkPlatforms } from '~/composables/useShoplinkPlatforms'
// Explicit — an auto-import that does not land here becomes an SSR
// ReferenceError, and the typecheck does not see into a plain `<script setup>`.
import { useShareMeta } from '~/composables/useShareMeta'

const props = defineProps({
    shortId: {
        type: String,
        required: true
    }
})

const shortId = computed(() => props.shortId)

const { data: shoplink, error } = await useFetch(() => `/api/public/shoplinks/${shortId.value}`)

if (error.value) {
    throw createError({ statusCode: 404, statusMessage: 'Shoplink not found' })
}

// Artwork
const artworkUrl = computed(() => {
    const art = shoplink.value?.release?.artwork
    if (!art) return null
    // 2026-09-14: `low` (250x250, ~84 KB) FIRST — the hero is a 208-px box and
    // the blurred backdrop; the master runs to several MB and streamed through
    // /api/assets/serve on every visit, which is why the page „lädt lange".
    // `thumbnail` (50x50) stays last, it would look like a stamp.
    const rawUrl = art.low?.url || art.original?.url || art.s3Url || art.url || art.thumbnail?.url || art.thumbnailUrl || null
    if (!rawUrl) return null
    return getProxiedUrl(rawUrl)
})

// Audio Playback
const selectedTrackIdx = ref(0)
const isPlaying = ref(false)
const audioPlayer = ref(null)

const getProxiedUrl = (url) => {
    if (!url || typeof url !== 'string') return ''
    if (url.startsWith('http')) return `/api/assets/serve?url=${encodeURIComponent(url)}`
    return url
}

const currentAudioUrl = computed(() => {
    const tracks = shoplink.value?.tracks
    const rawUrl = tracks?.length ? tracks[selectedTrackIdx.value]?.audioUrl : shoplink.value?.track?.audioUrl
    return getProxiedUrl(rawUrl)
})

const togglePlay = () => {
    if (!audioPlayer.value || !currentAudioUrl.value) return
    
    if (isPlaying.value) {
        audioPlayer.value.pause()
    } else {
        // Ensuring the source is loaded before playing
        if (audioPlayer.value.readyState === 0) {
            audioPlayer.value.load()
        }
        audioPlayer.value.play().catch(e => {
            console.error('[Audio] Playback failed:', e)
        })
    }
}

// Watch for URL changes and reload audio
watch(currentAudioUrl, () => {
    if (audioPlayer.value) {
        audioPlayer.value.load()
    }
})

const selectTrack = (idx) => {
    selectedTrackIdx.value = idx
    // Stop current playback when switching
    if (audioPlayer.value) {
        audioPlayer.value.pause()
        audioPlayer.value.currentTime = 0
    }
    isPlaying.value = false
    
    // Auto-play new track after brief delay (user already interacted by clicking)
    setTimeout(() => {
        if (audioPlayer.value && currentAudioUrl.value) {
            audioPlayer.value.play().catch(e => console.warn('[Audio] Auto-play blocked:', e))
        }
    }, 100)
}

onBeforeUnmount(() => {
    if (audioPlayer.value) {
        audioPlayer.value.pause()
        audioPlayer.value.src = ''
    }
})

// --- Pre-Release / Pre-Save ---
const now = ref(Date.now())
let countdownTimer = null

onMounted(() => {
    countdownTimer = setInterval(() => { now.value = Date.now() }, 1000)

    // Show success toast if redirected back from Spotify consent
    if (process.client) {
        const url = new URL(window.location.href)
        const status = url.searchParams.get('presave')
        if (status === 'ok') {
            presaved.value = true
            ElMessage.success('Pre-saved! We\'ll add this to your Spotify library on release day.')
            url.searchParams.delete('presave')
            window.history.replaceState({}, '', url.toString())
        } else if (status === 'denied') {
            ElMessage.warning('Pre-save cancelled.')
            url.searchParams.delete('presave')
            window.history.replaceState({}, '', url.toString())
        }
    }
})

onBeforeUnmount(() => {
    if (countdownTimer) clearInterval(countdownTimer)
})

const releaseDateMs = computed(() => {
    const d = shoplink.value?.release?.releaseDate
    return d ? new Date(d).getTime() : null
})

const isUpcoming = computed(() => {
    return releaseDateMs.value !== null && releaseDateMs.value > now.value
})

const countdownUnits = computed(() => {
    if (!isUpcoming.value) return [{ label: 'Days', value: 0 }, { label: 'Hours', value: 0 }, { label: 'Min', value: 0 }, { label: 'Sec', value: 0 }]
    let diff = Math.max(0, Math.floor((releaseDateMs.value - now.value) / 1000))
    const days = Math.floor(diff / 86400); diff -= days * 86400
    const hours = Math.floor(diff / 3600); diff -= hours * 3600
    const minutes = Math.floor(diff / 60); diff -= minutes * 60
    return [
        { label: 'Days', value: days },
        { label: 'Hours', value: hours },
        { label: 'Min', value: minutes },
        { label: 'Sec', value: diff },
    ]
})

const formattedReleaseDate = computed(() => {
    if (!releaseDateMs.value) return ''
    return new Date(releaseDateMs.value).toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric'
    })
})

const presaved = ref(false)

const startPresave = () => {
    if (!process.client) return
    const returnUrl = window.location.origin + window.location.pathname
    window.location.href = `/api/public/presave/start?shortId=${encodeURIComponent(shortId.value)}&returnUrl=${encodeURIComponent(returnUrl)}`
}

// Display info
const displayName = computed(() => shoplink.value?.release?.name || shoplink.value?.track?.name || shoplink.value?.name || '')
const displayArtist = computed(() => shoplink.value?.release?.displayArtist || shoplink.value?.track?.displayArtist || '')

// Gate logic
const { session, fetch: fetchSession } = useUserSession()
const completedTaskIds = ref([])
const verifyingTaskId = ref(null)
const connectingTaskId = ref(null)
const downloadingTrackId = ref(null)

const isFullyUnlocked = computed(() => {
    if (!shoplink.value?.downloadGate?.enabled) return true
    if (!shoplink.value?.downloadGate?.tasks?.length) return true
    return completedTaskIds.value.length >= shoplink.value.downloadGate.tasks.length
})

const isPlatformConnected = (platform) => {
    if (platform === 'spotify') return !!session.value?.fan?.spotify?.accessToken
    if (platform === 'soundcloud') return !!session.value?.fan?.soundcloud?.accessToken
    return false
}


async function handleTaskAction(task) {
    if (process.server) return
    if (!isPlatformConnected(task.platform)) {
        // Redirect to login with fan type and provider
        connectingTaskId.value = task._id
        const returnUrl = window.location.href
        window.location.href = `/api/auth/start?type=fan&provider=${task.platform}&next=${encodeURIComponent(returnUrl)}`
        return
    }

    // Verify/Perform task
    verifyingTaskId.value = task._id
    try {
        const res = await $fetch(`/api/public/shoplinks/${props.shortId}/verify-task`, {
            method: 'POST',
            body: { taskId: task._id }
        })

        if (res.success) {
            if (!completedTaskIds.value.includes(task._id)) {
                completedTaskIds.value.push(task._id)
            }
            if (process.client) ElMessage.success(res.message)
        } else {
            if (process.client) ElMessage.warning(res.message)
        }
    } catch (err) {
        console.error('[Action Error]', err)
        if (process.client) {
            const msg = err.data?.statusMessage || err.message || 'Action failed'
            ElMessage({
                message: String(msg),
                type: 'error',
                duration: 5000,
                showClose: true
            })
        }
    } finally {
        verifyingTaskId.value = null
    }
}

// Hydrate unlock state from the server.
//
// Completions are persisted per fan (ShoplinkTaskCompletion) and the
// download endpoint enforces against that store. Without this hydration
// the page would show every task as pending after a reload — the fan
// would be told to follow again while the server already considered
// them unlocked. Client-only for the same cookie reason as the watcher
// below.
async function hydrateGateStatus() {
    if (process.server) return
    if (!shoplink.value?.downloadGate?.enabled) return
    try {
        const status = await $fetch(`/api/public/shoplinks/${shortId.value}/gate-status`)
        if (Array.isArray(status?.completedTaskIds)) {
            completedTaskIds.value = status.completedTaskIds
        }
    } catch (e) {
        // Advisory call — a failure just means the fan re-verifies,
        // which is idempotent. Never block rendering on it.
        console.warn('[Gate] Could not hydrate gate status', e)
    }
}

onMounted(hydrateGateStatus)

// Auto-trigger tasks when a platform is connected on return from OAuth.
// Must be client-only: server-side $fetch does not forward browser cookies,
// so getUserSession() would return an empty session and throw 401.
watch(session, (newSession) => {
    if (process.server) return

    const tasks = shoplink.value?.downloadGate?.tasks
    if (!tasks) return

    if (newSession?.fan?.spotify?.accessToken) {
        tasks.forEach(task => {
            if (task.platform === 'spotify' && !completedTaskIds.value.includes(task._id)) {
                console.log('[Gate] Auto-triggering Spotify task for task:', task._id)
                setTimeout(() => handleTaskAction(task), 500)
            }
        })
    }

    if (newSession?.fan?.soundcloud?.accessToken) {
        tasks.forEach(task => {
            if (task.platform === 'soundcloud' && !completedTaskIds.value.includes(task._id)) {
                console.log('[Gate] Auto-triggering SoundCloud follow for task:', task._id)
                setTimeout(() => handleTaskAction(task), 500)
            }
        })
    }
}, { immediate: true })

const downloadTrack = (trackId, format) => {
    downloadingTrackId.value = trackId
    try {
        const url = `/api/public/shoplinks/${shortId.value}/download?trackId=${trackId}&format=${format}`

        // Create a hidden link element and trigger download
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', '') // Forces download instead of opening in browser
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        ElMessage.success(`Downloaded ${format.toUpperCase()} file`)

        // Reset session after download completes
        setTimeout(async () => {
            try {
                // Clear the fan session by logging out
                await $fetch('/api/auth/logout', { method: 'POST' })
                await fetchSession()
                ElMessage.info('Session reset. Complete the gate again to download more files.')
            } catch (err) {
                console.warn('[Session Reset] Error during logout:', err)
            }
            downloadingTrackId.value = null
        }, 1500)
    } catch (err) {
        console.error('[Download Error]', err)
        ElMessage.error('Download failed')
        downloadingTrackId.value = null
    }
}

const formatDuration = (seconds) => {
    if (!seconds) return '0:00'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
}

// Task labels
const getTaskLabel = (task) => {
    const platform = task.platform === 'spotify' ? 'Spotify' : 'SoundCloud'
    if (task.action === 'follow_artist') return `Follow on ${platform}`
    if (task.action === 'follow_playlist') return `Follow Playlist on ${platform}`
    return task.action
}

// DSP logos + display names (shared with the creator/admin editors)
const { getDspLogo, formatPlatformName } = useShoplinkPlatforms()

// SEO / link preview
//
// This is the link artists post everywhere, so the preview has to name the
// release AND the artist and say what the link does. It used to carry only
// og:title = the release name: no artist, no description, no og:url. The
// page stays noindex (the catch-all route sets it); crawlers of chat apps
// and social networks read the tags regardless.
//
// The image is the stored master; useShareMeta routes it through the proxy
// at 1200 px, so it is sharp, absolute, fetchable by an anonymous crawler
// and well under X's 5 MB share limit.
const { appName: shopName } = useTenantTheme()
const sharePlatforms = computed(() => {
    const names = [...new Set((shoplink.value?.links || []).map(l => formatPlatformName(l.platform)).filter(Boolean))]
    if (names.length <= 3) return names.join(', ').replace(/, ([^,]*)$/, ' and $1')
    return `${names.slice(0, 3).join(', ')} and more`
})
useShareMeta(() => {
    const s = shoplink.value
    if (!s) return null
    const name = displayName.value
    const artist = displayArtist.value
    const what = artist ? `${name} by ${artist}` : name
    let description
    if (isUpcoming.value) description = `Pre-save ${what}${releaseDateMs.value ? ` — out ${new Date(releaseDateMs.value).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })}` : ''}.`
    else if (sharePlatforms.value) description = `Listen to ${what} on ${sharePlatforms.value}.`
    else description = `Listen to ${what}.`
    if (s.downloadGate?.enabled) description += ' Free download available.'
    const art = s.release?.artwork || {}
    const artUrl = v => (typeof v === 'string' ? v : v?.url) || ''
    return {
        title: artist ? `${name} – ${artist}` : name,
        description,
        image: artUrl(art.original) || art.s3Url || artUrl(art.low) || artUrl(art) || artUrl(art.thumbnail) || art.thumbnailUrl || '',
        imageAlt: artist ? `${name} – ${artist}` : name,
        type: s.type === 'track' ? 'music.song' : 'music.album',
    }
}, { notFoundTitle: shopName.value, localeAlternates: false })
</script>

<style scoped>
@keyframes pulse-slow {
    0%, 100% { transform: scale(1); opacity: 0.9; }
    50% { transform: scale(1.05); opacity: 1; }
}
.animate-pulse-slow {
    animation: pulse-slow 2s infinite ease-in-out;
}

.slide-down-enter-active,
.slide-down-leave-active {
    transition: all 0.3s ease;
}
.slide-down-enter-from,
.slide-down-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}
</style>
